import './index.css';
import { AudioEngine } from './audio/engine';
import { DropZone } from './ui/drop-zone';
import { UnifiedGridVisualizer } from './ui/visualizer';
import type { LayerType } from './types';
import type { RecordingTarget } from './audio/recorder';
import {
  saveSample,
  getSampleList,
  getSample,
  deleteSample,
  sampleToFile,
  formatFileSize,
  formatDuration,
} from './lib/storage';

// Import new modules
import { initIcons, updateLockIcon, updateMuteIconById, updatePlayIcon } from './ui/icons';
import { updateStatus } from './ui/status';
import { toggleTheme } from './ui/theme';
import { generateSampleId } from './utils/id-generator';
import { togglePlayback, randomizeParams, updateAudioParams } from './handlers/playback';
import {
  toggleRecording,
  exportRecordings,
} from './handlers/recording';
import { toggleLibraryPanel } from './handlers/library';
import {
  showSavePresetModal,
  showLoadPresetModal,
} from './handlers/presets';
import {
  syncVisualizerLayerStates,
  updateLayerTypeVolume,
  showAddLayerMenu,
  renderLayersList,
} from './handlers/layers';
import type { LoadedSample } from './handlers/samples';
import { renderSamplesList, updateDropZoneState } from './handlers/samples';

// GenMus - Minimalist Procedural Music Tool
console.log('GenMus initializing...');

// Get audio engine instance
const audioEngine = AudioEngine.getInstance();

// UI State
interface LayerState {
  id: string;
  type: LayerType;
  label: string;
  muted: boolean;
  volume: number;
  isAdditional?: boolean;
}

const layerStates: LayerState[] = [
  { id: 'percussion-0', type: 'percussion', label: 'PERC', muted: false, volume: 0.25 },
  { id: 'bass-0', type: 'bass', label: 'BASS', muted: false, volume: 0.25 },
  { id: 'melody-0', type: 'melody', label: 'MELO', muted: false, volume: 0.25 },
  { id: 'pad-0', type: 'pad', label: 'PAD', muted: false, volume: 0.25 },
];

// Counter for generating unique layer IDs
let layerIdCounter = 1;

// Multi-sample state
const loadedSamples: LoadedSample[] = [];
let activeSampleId: string | null = null;

// Visualizers
let unifiedVisualizer: UnifiedGridVisualizer;

// Current sample ID (if loaded from library)
let currentSampleId: string | null = null;

// Current file (for saving to library)
let currentFile: File | null = null;

// Recording state
let recordingState = {
  isRecording: false,
  recordingStartTime: 0,
  recordingTimer: null as number | null,
  recordedBlobs: null as Map<RecordingTarget, Blob> | null,
};

// Initialize theme before rendering to prevent flash
const savedTheme = localStorage.getItem('genmus-theme') || 'light';
document.documentElement.setAttribute('data-theme', savedTheme);

// App entry point
const app = document.querySelector<HTMLDivElement>('#app')!;

// Render UI - Visualizer on top, samples and controls on bottom
app.innerHTML = `
  <div class="app-container">
    <!-- Top Row: Full Width Visualization - Drop Zone -->
    <section id="drop-zone" class="visualization-section drop-zone-area">
      <!-- Drop Overlay (shows on drag or when no sample) -->
      <div class="drop-overlay">
        <div class="drop-zone-content">
          <span id="icon-waves"></span>
          <span class="drop-text">Drop audio file</span>
          <span class="drop-hint">MP3, WAV, OGG, FLAC</span>
        </div>
      </div>

      <!-- Unified Visualizer Canvas -->
      <div id="unified-visualizer" class="visualizer-canvas-container"></div>

      <!-- Top-right toolbar: Play, Randomize, Theme -->
      <div class="top-toolbar">
        <span id="status-text" class="status-text">Drop a sample</span>
        <button id="play-pause-btn" class="w-9 h-9 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] flex items-center justify-center cursor-pointer transition-all duration-150 text-[var(--color-foreground)] hover:bg-[var(--color-foreground)] hover:text-[var(--color-background)] hover:border-[var(--color-foreground)]" title="Play/Stop">
          <span id="icon-play"></span>
        </button>
        <button id="randomize-btn" class="w-9 h-9 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] flex items-center justify-center cursor-pointer transition-all duration-150 text-[var(--color-foreground)] hover:bg-[var(--color-foreground)] hover:text-[var(--color-background)] hover:border-[var(--color-foreground)]" title="Randomize">
          <span id="icon-shuffle"></span>
        </button>
        <button id="theme-toggle-btn" class="w-9 h-9 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] flex items-center justify-center cursor-pointer transition-all duration-150 text-[var(--color-foreground)] hover:bg-[var(--color-foreground)] hover:text-[var(--color-background)] hover:border-[var(--color-foreground)]" title="Toggle Theme">
          <span id="icon-theme"></span>
        </button>
      </div>
    </section>

    <!-- Bottom Controls Bar - 5 Column Grid -->
    <div class="bottom-panel">
      <!-- Samples -->
      <div class="samples-section">
        <div class="section-title">
          <span>Samples</span>
          <div class="section-actions">
            <button id="save-sample-btn" class="w-[26px] h-[26px] rounded border border-transparent bg-transparent flex items-center justify-center cursor-pointer transition-all duration-150 text-[var(--color-muted-foreground)] text-[0.65rem] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]" title="Save to Library" style="display: none;">
              <span id="icon-save-sample"></span>
            </button>
            <button id="library-btn" class="w-[26px] h-[26px] rounded border border-transparent bg-transparent flex items-center justify-center cursor-pointer transition-all duration-150 text-[var(--color-muted-foreground)] text-[0.65rem] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]" title="Library">
              <span id="icon-library"></span>
            </button>
          </div>
        </div>
        <div class="samples-list" id="samples-list">
          <div class="sample-empty-state">Drop samples to begin</div>
        </div>
      </div>

      <!-- Layers -->
      <div class="control-section">
        <div class="section-title">
          <span>Layers</span>
          <button id="add-layer-btn" class="w-[26px] h-[26px] rounded border border-transparent bg-transparent flex items-center justify-center cursor-pointer transition-all duration-150 text-[var(--color-muted-foreground)] text-[0.65rem] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]" title="Add Layer">
            <span id="icon-add-layer"></span>
          </button>
        </div>
        <div class="control-rows" id="layers-list">
          ${layerStates.map((layer) => `
            <div class="control-row-inline" data-layer-id="${layer.id}" data-layer="${layer.type}">
              <button class="w-6 h-6 rounded border border-[var(--color-border)] bg-transparent flex items-center justify-center cursor-pointer transition-all duration-150 text-[var(--color-foreground)] flex-shrink-0 hover:bg-[var(--color-muted)]" data-layer-id="${layer.id}" data-layer="${layer.type}" title="${layer.muted ? 'Unmute' : 'Mute'}">
                <span class="mute-icon" data-layer-id="${layer.id}"></span>
              </button>
              <span class="control-label">${layer.label}</span>
              <input type="range" class="slider-input layer-volume" data-layer-id="${layer.id}" data-layer="${layer.type}" min="0" max="100" value="25" />
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Transport -->
      <div class="control-section">
        <div class="section-title">Transport</div>
        <div class="control-rows">
          <div class="control-row-inline">
            <span class="control-label">Tempo</span>
            <input type="range" id="tempo-slider" class="slider-input" min="60" max="180" value="120" />
            <span id="tempo-value" class="control-value">120</span>
          </div>
          <div class="control-row-inline">
            <span class="control-label">Density</span>
            <input type="range" id="density-slider" class="slider-input" min="0" max="100" value="75" />
            <span id="density-value" class="control-value">75%</span>
          </div>
          <div class="control-row-inline">
            <span class="control-label">Mood</span>
            <input type="range" id="mood-slider" class="slider-input" min="0" max="100" value="50" />
            <span id="mood-value" class="control-value">50</span>
          </div>
          <div class="control-row-inline">
            <button id="lock-btn" class="lock-btn" title="Lock patterns (prevent changes during playback)">
              <span id="icon-lock"></span>
            </button>
            <span class="control-label">Evolve</span>
            <input type="range" id="variation-slider" class="slider-input" min="0" max="100" value="30" />
            <span id="variation-value" class="control-value">30%</span>
          </div>
        </div>
      </div>

      <!-- Effects -->
      <div class="control-section">
        <div class="section-title">Effects</div>
        <div class="control-rows">
          <div class="control-row-inline">
            <span class="control-label">Reverb</span>
            <input type="range" id="reverb-slider" class="slider-input" min="0" max="100" value="20" />
            <span id="reverb-value" class="control-value">20%</span>
          </div>
          <div class="control-row-inline">
            <span class="control-label">Delay</span>
            <input type="range" id="delay-slider" class="slider-input" min="0" max="100" value="15" />
            <span id="delay-value" class="control-value">15%</span>
          </div>
          <div class="control-row-inline">
            <span class="control-label">Compress</span>
            <input type="range" id="compression-slider" class="slider-input" min="0" max="100" value="30" />
            <span id="compression-value" class="control-value">30%</span>
          </div>
          <div class="control-row-inline">
            <span class="control-label">Saturate</span>
            <input type="range" id="saturation-slider" class="slider-input" min="0" max="100" value="0" />
            <span id="saturation-value" class="control-value">0%</span>
          </div>
          <div class="control-row-inline">
            <span class="control-label">Smooth</span>
            <input type="range" id="smoothness-slider" class="slider-input" min="0" max="100" value="30" />
            <span id="smoothness-value" class="control-value">30%</span>
          </div>
        </div>
      </div>

      <!-- Recording -->
      <div class="control-section">
        <div class="section-title">Record</div>
        <div class="control-rows recording-controls">
          <div class="recording-status">
            <span id="recording-indicator" class="recording-indicator"></span>
            <span id="recording-time" class="recording-time">00:00</span>
          </div>
          <button id="record-btn" class="flex items-center justify-center gap-2 flex-1 min-h-[28px] rounded-md border border-[var(--color-border)] bg-transparent text-[0.65rem] font-medium text-[var(--color-foreground)] cursor-pointer transition-all duration-150 hover:bg-[var(--color-muted)]" title="Start Recording">
            <span id="icon-record"></span>
            <span id="record-btn-text">Record</span>
          </button>
          <button id="export-btn" class="flex items-center justify-center gap-2 flex-1 rounded-md border border-[var(--color-border)] bg-transparent text-xs font-medium text-[var(--color-foreground)] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-muted)]" title="Export Recordings" style="display: none;">
            <span id="icon-download"></span>
            Export All
          </button>
        </div>
      </div>

      <!-- Presets -->
      <div class="control-section">
        <div class="section-title">Presets</div>
        <div class="control-rows presets-grid">
          <button id="save-preset-btn" class="flex items-center justify-center gap-2 flex-1 rounded-md border border-[var(--color-border)] bg-transparent text-xs font-medium text-[var(--color-foreground)] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-muted)]">
            <span id="icon-save"></span>
            Save
          </button>
          <button id="load-preset-btn" class="flex items-center justify-center gap-2 flex-1 rounded-md border border-[var(--color-border)] bg-transparent text-xs font-medium text-[var(--color-foreground)] cursor-pointer transition-colors duration-150 hover:bg-[var(--color-muted)]">
            <span id="icon-folder"></span>
            Load
          </button>
        </div>
      </div>
    </div>

    <!-- Library Panel (hidden by default) -->
    <div id="library-panel" class="library-panel">
      <div class="library-header">
        <span>Sample Library</span>
        <button id="close-library-btn" class="w-[26px] h-[26px] rounded border border-transparent bg-transparent flex items-center justify-center cursor-pointer transition-all duration-150 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]">
          <span id="icon-close-library"></span>
        </button>
      </div>
      <div id="library-list" class="library-list">
        <div class="empty-state">No samples saved</div>
      </div>
    </div>
  </div>
`;

// Helper functions that need access to state
function handleMuteToggle(layerId: string): void {
  const state = layerStates.find(l => l.id === layerId);
  if (state) {
    state.muted = !state.muted;
    updateLayerTypeVolume(state.type, layerStates, audioEngine);
    updateMuteIconById(layerId, state.muted);
    syncVisualizerLayerStates(layerStates, unifiedVisualizer);
  }
}

function handleVolumeChange(layerId: string, volume: number): void {
  const state = layerStates.find(l => l.id === layerId);
  if (state) {
    state.volume = volume;
    updateLayerTypeVolume(state.type, layerStates, audioEngine);
    syncVisualizerLayerStates(layerStates, unifiedVisualizer);
  }
}

function handleAddLayer(type: LayerType): void {
  const newLayer: LayerState = {
    id: `${type}-${layerIdCounter++}`,
    type,
    label: type.toUpperCase().slice(0, 4),
    muted: false,
    volume: 0.25,
    isAdditional: true
  };
  layerStates.push(newLayer);
  renderLayersList(
    layerStates,
    handleMuteToggle,
    handleVolumeChange,
    handleRemoveLayer
  );
  initIcons();
  syncVisualizerLayerStates(layerStates, unifiedVisualizer);
}

function handleRemoveLayer(layerId: string): void {
  const index = layerStates.findIndex(l => l.id === layerId);
  if (index !== -1) {
    layerStates.splice(index, 1);
    renderLayersList(
      layerStates,
      handleMuteToggle,
      handleVolumeChange,
      handleRemoveLayer
    );
    initIcons();
    syncVisualizerLayerStates(layerStates, unifiedVisualizer);
  }
}

async function handleAddSample(file: File): Promise<void> {
  const id = generateSampleId();
  const sample: LoadedSample = { id, name: file.name, file };
  const isFirstSample = loadedSamples.length === 0;
  loadedSamples.push(sample);

  if (isFirstSample) {
    try {
      await audioEngine.loadSample(file);
      activeSampleId = id;
      currentFile = file;
      updateStatus(`Loaded: ${file.name}`, false);
      
      // Auto-play when first sample is uploaded
      if (!audioEngine.getIsPlaying()) {
        audioEngine.start();
        unifiedVisualizer.start();
        updatePlayIcon(true);
        updateStatus('Playing', true);
      }
    } catch (error) {
      console.error('Error loading sample:', error);
      updateStatus('Error loading sample', false);
    }
  }

  renderSamplesList(
    loadedSamples,
    activeSampleId,
    handleRemoveSample,
    handleSwitchSample
  );
  updateDropZoneState(loadedSamples.length > 0);

  const saveSampleBtn = document.getElementById('save-sample-btn');
  if (saveSampleBtn && loadedSamples.length > 0) {
    saveSampleBtn.style.display = 'flex';
  }
}

function handleRemoveSample(id: string): void {
  const index = loadedSamples.findIndex(s => s.id === id);
  if (index !== -1) {
    loadedSamples.splice(index, 1);
  }

  if (id === activeSampleId && loadedSamples.length > 0) {
    activeSampleId = loadedSamples[0].id;
  } else if (loadedSamples.length === 0) {
    activeSampleId = null;
  }

  renderSamplesList(
    loadedSamples,
    activeSampleId,
    handleRemoveSample,
    handleSwitchSample
  );
  updateDropZoneState(loadedSamples.length > 0);
}

async function handleSwitchSample(id: string): Promise<void> {
  const sample = loadedSamples.find(s => s.id === id);
  if (sample) {
    try {
      await audioEngine.loadSample(sample.file);
      activeSampleId = id;
      currentFile = sample.file;
      updateStatus(`Loaded: ${sample.name}`, false);
      renderSamplesList(
        loadedSamples,
        activeSampleId,
        handleRemoveSample,
        handleSwitchSample
      );
    } catch (error) {
      console.error('Error loading sample:', error);
      updateStatus('Error loading sample', false);
    }
  }
}

async function renderSampleLibrary(): Promise<void> {
  const samples = await getSampleList();
  const libraryList = document.getElementById('library-list');
  if (!libraryList) return;

  if (samples.length === 0) {
    libraryList.innerHTML = '<div class="empty-state">No samples saved</div>';
    return;
  }

  libraryList.innerHTML = samples.map(sample => `
    <div class="library-item" data-sample-id="${sample.id}">
      <div>
        <div class="library-item-name">${sample.name}</div>
        <div class="library-item-meta">${formatFileSize(sample.size)} • ${formatDuration(sample.duration)}</div>
      </div>
      <button class="library-item-btn delete" data-delete-sample="${sample.id}" title="Delete">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
        </svg>
      </button>
    </div>
  `).join('');

  libraryList.querySelectorAll('.library-item').forEach(item => {
    item.addEventListener('click', async (e) => {
      if ((e.target as HTMLElement).closest('[data-delete-sample]')) return;

      const sampleId = (item as HTMLElement).dataset.sampleId;
      if (sampleId) {
        await loadSampleFromLibrary(sampleId);
      }
    });
  });

  libraryList.querySelectorAll('[data-delete-sample]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const sampleId = (btn as HTMLElement).dataset.deleteSample;
      if (sampleId && confirm('Delete this sample from library?')) {
        await deleteSample(sampleId);
        await renderSampleLibrary();
      }
    });
  });
}

async function loadSampleFromLibrary(sampleId: string): Promise<void> {
  try {
    const sampleData = await getSample(sampleId);
    if (sampleData) {
      const file = await sampleToFile(sampleData);
      await handleAddSample(file);
      currentSampleId = sampleId;
      toggleLibraryPanel(renderSampleLibrary);
    }
  } catch (error) {
    console.error('Error loading sample:', error);
    updateStatus('Error loading sample', false);
  }
}

// Initialize UI
function initUI(): void {
  // Initialize icons
  initIcons();

  // Initialize visualizer
  unifiedVisualizer = new UnifiedGridVisualizer();
  unifiedVisualizer.init('unified-visualizer');
  syncVisualizerLayerStates(layerStates, unifiedVisualizer);

  // Playback controls
  document.getElementById('play-pause-btn')?.addEventListener('click', () => {
    togglePlayback(audioEngine, unifiedVisualizer);
  });

  document.getElementById('randomize-btn')?.addEventListener('click', () => {
    randomizeParams(audioEngine);
  });

  document.getElementById('theme-toggle-btn')?.addEventListener('click', () => {
    toggleTheme(unifiedVisualizer);
  });

  // Transport sliders
  document.getElementById('tempo-slider')?.addEventListener('input', (e) => {
    const value = (e.target as HTMLInputElement).value;
    document.getElementById('tempo-value')!.textContent = value;
    updateAudioParams(audioEngine);
  });

  document.getElementById('density-slider')?.addEventListener('input', (e) => {
    const value = (e.target as HTMLInputElement).value;
    document.getElementById('density-value')!.textContent = `${value}%`;
    updateAudioParams(audioEngine);
  });

  document.getElementById('mood-slider')?.addEventListener('input', (e) => {
    const value = (e.target as HTMLInputElement).value;
    document.getElementById('mood-value')!.textContent = value;
    updateAudioParams(audioEngine);
  });

  document.getElementById('variation-slider')?.addEventListener('input', (e) => {
    const value = (e.target as HTMLInputElement).value;
    document.getElementById('variation-value')!.textContent = `${value}%`;
    audioEngine.setLiveVariation(parseInt(value));
  });

  document.getElementById('lock-btn')?.addEventListener('click', () => {
    const locked = !audioEngine.isPatternsLocked();
    audioEngine.setPatternLock(locked);
    updateLockIcon(locked);
  });

  // Effects sliders
  document.getElementById('reverb-slider')?.addEventListener('input', (e) => {
    const value = parseInt((e.target as HTMLInputElement).value);
    document.getElementById('reverb-value')!.textContent = `${value}%`;
    audioEngine.setReverbAmount(value / 100);
  });

  document.getElementById('delay-slider')?.addEventListener('input', (e) => {
    const value = parseInt((e.target as HTMLInputElement).value);
    document.getElementById('delay-value')!.textContent = `${value}%`;
    audioEngine.setDelayAmount(value / 100);
  });

  document.getElementById('compression-slider')?.addEventListener('input', (e) => {
    const value = parseInt((e.target as HTMLInputElement).value);
    document.getElementById('compression-value')!.textContent = `${value}%`;
    audioEngine.setCompressionAmount(value / 100);
  });

  document.getElementById('saturation-slider')?.addEventListener('input', (e) => {
    const value = parseInt((e.target as HTMLInputElement).value);
    document.getElementById('saturation-value')!.textContent = `${value}%`;
    audioEngine.setSaturationAmount(value / 100);
  });

  document.getElementById('smoothness-slider')?.addEventListener('input', (e) => {
    const value = parseInt((e.target as HTMLInputElement).value);
    document.getElementById('smoothness-value')!.textContent = `${value}%`;
    audioEngine.setSmoothness(value);
  });

  // Layer controls - delegate to handlers
  document.getElementById('add-layer-btn')?.addEventListener('click', () => {
    showAddLayerMenu(handleAddLayer);
  });

  // Setup initial layer controls
  renderLayersList(
    layerStates,
    handleMuteToggle,
    handleVolumeChange,
    handleRemoveLayer
  );

  // Recording
  document.getElementById('record-btn')?.addEventListener('click', async () => {
    recordingState = await toggleRecording(
      audioEngine,
      unifiedVisualizer,
      recordingState
    );
  });

  document.getElementById('export-btn')?.addEventListener('click', () => {
    exportRecordings(recordingState.recordedBlobs, audioEngine);
  });

  // Presets
  document.getElementById('save-preset-btn')?.addEventListener('click', () => {
    showSavePresetModal(layerStates, currentSampleId, audioEngine);
  });

  document.getElementById('load-preset-btn')?.addEventListener('click', () => {
    showLoadPresetModal(
      audioEngine,
      layerStates,
      loadSampleFromLibrary,
      (layerType) => updateLayerTypeVolume(layerType, layerStates, audioEngine),
      updateMuteIconById,
      () => syncVisualizerLayerStates(layerStates, unifiedVisualizer)
    );
  });

  // Library
  document.getElementById('library-btn')?.addEventListener('click', () => {
    toggleLibraryPanel(renderSampleLibrary);
  });

  document.getElementById('close-library-btn')?.addEventListener('click', () => {
    toggleLibraryPanel(renderSampleLibrary);
  });

  document.getElementById('save-sample-btn')?.addEventListener('click', async () => {
    if (currentFile && audioEngine.getBuffer()) {
      await saveSample(currentFile, audioEngine.getBufferDuration());
      updateStatus('Sample saved to library!', false);
      setTimeout(() => updateStatus(audioEngine.getIsPlaying() ? 'Playing' : 'Ready', audioEngine.getIsPlaying()), 1500);
    }
  });
}

// Initialize
initUI();

// Set initial smoothness
audioEngine.setSmoothness(30);

// Set initial compression (30% = moderate compression)
audioEngine.setCompressionAmount(0.3);

// Set initial saturation (0% = clean signal)
audioEngine.setSaturationAmount(0);

// Set initial live variation
audioEngine.setLiveVariation(30);

// Initialize drop zone
new DropZone('drop-zone', async (file: File) => {
  try {
    await handleAddSample(file);
  } catch (error) {
    console.error('Error adding sample:', error);
    updateStatus('Error loading sample', false);
  }
});

// Set initial drop zone state
updateDropZoneState(loadedSamples.length > 0);
